import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  ArrowLeft, Search, Trash2, Pencil, Filter, X, AlertTriangle,
  CheckCircle, Building2, Calendar, Clock, Loader2, RotateCcw
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { GroupTransferModal } from './GroupTransferModal'
import { lignesDe, reglementACoteDeLAnnulation } from '../../../lib/inscriptions/reglement'
import { useReglementDesInscriptions } from '../../../lib/inscriptions/useReglementDesInscriptions'
import { PastilleDeReglement } from '../../../components/admin/PastilleDeReglement'
import { estUneJpa } from '../../../lib/jpa/regles'

interface KidSuggestion {
  id: string
  nom: string
  prenom: string
  date_naissance: string
}

interface RegistrationRow {
  id: string
  kid_id: string
  activity_id: string
  payment_status: string
  cancellation_status: string
  amount_paid: number
  price_type: string
  subgroup_id: string | null
  subgroup_name: string | null
  created_at: string
  session: {
    id: string
    start_date: string
    end_date: string
    periode: string
    semaine: string
    center_id: string
    capacity: number
    current_registrations: number
    stage: { id: string; title: string; type_activite?: string | null }
    center: { id: string; name: string }
  }
}

interface Center {
  id: string
  name: string
}

interface Props {
  onBack: () => void
}

export function RegistrationModification({ onBack }: Props) {
  const { userProfile } = useAuth()
  const isAdmin = userProfile?.role === 'admin'

  const [searchQuery, setSearchQuery] = useState('')
  const [suggestions, setSuggestions] = useState<KidSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedKid, setSelectedKid] = useState<KidSuggestion | null>(null)
  const [registrations, setRegistrations] = useState<RegistrationRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const [centers, setCenters] = useState<Center[]>([])
  const [filterCenter, setFilterCenter] = useState('')
  const [filterPeriode, setFilterPeriode] = useState('')
  const [filterSemaine, setFilterSemaine] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<RegistrationRow | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [reactivateTarget, setReactivateTarget] = useState<RegistrationRow | null>(null)
  const [reactivating, setReactivating] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const [transferTarget, setTransferTarget] = useState<RegistrationRow | null>(null)

  useEffect(() => {
    supabase.from('centers').select('id, name').eq('active', true).order('name')
      .then(({ data }) => setCenters(data || []))
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (searchQuery.length < 2) { setSuggestions([]); return }
    const timeout = setTimeout(async () => {
      setSearchLoading(true)
      const q = searchQuery.trim()
      const { data } = await supabase
        .from('kids')
        .select('id, nom, prenom, date_naissance')
        .or(`nom.ilike.%${q}%,prenom.ilike.%${q}%`)
        .order('nom')
        .limit(15)
      setSuggestions(data || [])
      setShowSuggestions(true)
      setSearchLoading(false)
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchQuery])

  const loadRegistrations = useCallback(async (kidId: string) => {
    setLoading(true)
    const { data, error } = await supabase
      .from('registrations')
      .select(`
        id, kid_id, activity_id, payment_status, cancellation_status,
        amount_paid, price_type, subgroup_id, subgroup_name, created_at,
        session:sessions!activity_id(
          id, start_date, end_date, periode, semaine, center_id,
          capacity, current_registrations,
          stage:stages(id, title, type_activite),
          center:centers(id, name)
        )
      `)
      .eq('kid_id', kidId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error loading registrations:', error)
      setErrorMessage('Erreur lors du chargement des inscriptions.')
    } else {
      setRegistrations((data as any) || [])
    }
    setLoading(false)
  }, [])

  const selectKid = (kid: KidSuggestion) => {
    setSelectedKid(kid)
    setSearchQuery(`${kid.prenom} ${kid.nom}`)
    setShowSuggestions(false)
    loadRegistrations(kid.id)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    setErrorMessage('')

    const { error } = await supabase
      .from('registrations')
      .delete()
      .eq('id', deleteTarget.id)

    if (error) {
      console.error('Delete error:', error)
      setErrorMessage('Erreur lors de la suppression.')
    } else {
      setSuccessMessage('Inscription supprimee avec succes.')
      if (selectedKid) loadRegistrations(selectedKid.id)
      setTimeout(() => setSuccessMessage(''), 5000)
    }
    setDeleteTarget(null)
    setDeleting(false)
  }

  const handleReactivate = async () => {
    if (!reactivateTarget) return
    setReactivating(true)
    setErrorMessage('')

    const { error } = await supabase
      .from('registrations')
      .update({ cancellation_status: 'none' })
      .eq('id', reactivateTarget.id)

    if (error) {
      console.error('Reactivate error:', error)
      setErrorMessage('Erreur lors de la reactivation.')
    } else {
      setSuccessMessage('Inscription reactivee avec succes.')
      if (selectedKid) loadRegistrations(selectedKid.id)
      setTimeout(() => setSuccessMessage(''), 5000)
    }
    setReactivateTarget(null)
    setReactivating(false)
  }

  const handleTransferSuccess = () => {
    setTransferTarget(null)
    setSuccessMessage('Transfert effectue avec succes.')
    if (selectedKid) loadRegistrations(selectedKid.id)
    setTimeout(() => setSuccessMessage(''), 5000)
  }

  // La colonne « Paiement » dit la facture, plus `payment_status` ; l'annulation
  // a sa propre colonne. Relu à chaque chargement des inscriptions de l'enfant.
  const registrationIds = useMemo(() => registrations.map(r => r.id), [registrations])
  const reglements = useReglementDesInscriptions(registrationIds)

  const today = new Date().toISOString().split('T')[0]

  const getRowStatus = (reg: RegistrationRow): 'cancelled' | 'past' | 'active' => {
    if (reg.cancellation_status !== 'none') return 'cancelled'
    if (reg.session.end_date < today) return 'past'
    return 'active'
  }

  const availablePeriodes = [...new Set(registrations.map(r => r.session.periode))].sort()
  const availableSemaines = [...new Set(
    registrations
      .filter(r => !filterPeriode || r.session.periode === filterPeriode)
      .map(r => r.session.semaine)
  )].sort()

  const filtered = registrations.filter(r => {
    if (filterCenter && r.session.center_id !== filterCenter) return false
    if (filterPeriode && r.session.periode !== filterPeriode) return false
    if (filterSemaine && r.session.semaine !== filterSemaine) return false
    return true
  })

  const rowClasses: Record<string, string> = {
    active: 'bg-green-50 border-l-4 border-l-green-500',
    past: 'bg-gray-50 border-l-4 border-l-gray-300 opacity-75',
    cancelled: 'bg-red-50 border-l-4 border-l-red-400',
  }

  const cancellationLabel: Record<string, string> = {
    none: '',
    requested: 'Demande',
    cancelled_full_refund: 'Rembourse',
    cancelled_partial_refund: 'Partiel',
    cancelled_no_refund: 'Non rembourse',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modification / Suppression</h1>
          <p className="text-gray-600">Rechercher un enfant pour gerer ses inscriptions</p>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800 text-sm font-medium">{successMessage}</p>
          <button onClick={() => setSuccessMessage('')} className="ml-auto text-green-600 hover:text-green-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800 text-sm font-medium">{errorMessage}</p>
          <button onClick={() => setErrorMessage('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div ref={searchRef} className="relative max-w-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher un enfant</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Tapez un nom ou prenom..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value)
                if (selectedKid) {
                  setSelectedKid(null)
                  setRegistrations([])
                }
              }}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
            )}
            {searchQuery && !searchLoading && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setSelectedKid(null)
                  setRegistrations([])
                  setSuggestions([])
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map(kid => (
                <button
                  key={kid.id}
                  onClick={() => selectKid(kid)}
                  className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <p className="text-sm font-medium text-gray-900">{kid.prenom} {kid.nom}</p>
                  <p className="text-xs text-gray-500">
                    Ne(e) le {new Date(kid.date_naissance).toLocaleDateString('fr-BE')}
                  </p>
                </button>
              ))}
            </div>
          )}
          {showSuggestions && searchQuery.length >= 2 && suggestions.length === 0 && !searchLoading && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
              <p className="text-sm text-gray-500 text-center">Aucun enfant trouve</p>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {selectedKid && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Selected kid header + filters */}
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedKid.prenom} {selectedKid.nom}
              </h2>
              <p className="text-sm text-gray-500">
                {registrations.length} inscription{registrations.length !== 1 ? 's' : ''} trouvee{registrations.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showFilters || filterCenter || filterPeriode || filterSemaine
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtres
              {(filterCenter || filterPeriode || filterSemaine) && (
                <span className="w-2 h-2 rounded-full bg-blue-600" />
              )}
            </button>
          </div>

          {/* Filter bar */}
          {showFilters && (
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-400" />
                <select
                  value={filterCenter}
                  onChange={e => setFilterCenter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Tous les centres</option>
                  {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <select
                  value={filterPeriode}
                  onChange={e => { setFilterPeriode(e.target.value); setFilterSemaine('') }}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Toutes les periodes</option>
                  {availablePeriodes.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <select
                  value={filterSemaine}
                  onChange={e => setFilterSemaine(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Toutes les semaines</option>
                  {availableSemaines.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {(filterCenter || filterPeriode || filterSemaine) && (
                <button
                  onClick={() => { setFilterCenter(''); setFilterPeriode(''); setFilterSemaine('') }}
                  className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Effacer
                </button>
              )}
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-sm">
              Aucune inscription trouvee{(filterCenter || filterPeriode || filterSemaine) ? ' pour ces filtres' : ''}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-6 py-3">Stage</th>
                    <th className="px-4 py-3">Centre</th>
                    <th className="px-4 py-3">Periode</th>
                    <th className="px-4 py-3">Semaine</th>
                    <th className="px-4 py-3">Dates</th>
                    <th className="px-4 py-3">Paiement</th>
                    <th className="px-4 py-3">Annulation</th>
                    <th className="px-4 py-3">Groupe</th>
                    {isAdmin && <th className="px-4 py-3 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(reg => {
                    const status = getRowStatus(reg)
                    const isCancelled = reg.cancellation_status !== 'none'

                    return (
                      <tr key={reg.id} className={rowClasses[status]}>
                        <td className="px-6 py-3 font-medium text-gray-900">{reg.session.stage?.title}</td>
                        <td className="px-4 py-3 text-gray-600">{reg.session.center?.name}</td>
                        <td className="px-4 py-3 text-gray-600">{reg.session.periode}</td>
                        <td className="px-4 py-3 text-gray-600">{reg.session.semaine}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {new Date(reg.session.start_date).toLocaleDateString('fr-BE')} - {new Date(reg.session.end_date).toLocaleDateString('fr-BE')}
                        </td>
                        <td className="px-4 py-3">
                          <PastilleDeReglement
                            statut={reglementACoteDeLAnnulation(
                              { annulation: reg.cancellation_status, commande: reg.payment_status },
                              lignesDe(reglements, reg.id),
                            )}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {isCancelled && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              {cancellationLabel[reg.cancellation_status] || reg.cancellation_status}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{reg.subgroup_name || '-'}</td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              {/* Une journee pedagogique ne change pas de groupe : aucune destination possible */}
                              {!estUneJpa(reg.session?.stage) && (
                                <button
                                  onClick={() => setTransferTarget(reg)}
                                  title="Modifier l'inscription"
                                  className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-100 transition-colors"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                              )}
                              {isCancelled && (
                                <>
                                  <button
                                    onClick={() => setReactivateTarget(reg)}
                                    title="Reactiver l'inscription"
                                    className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-100 transition-colors"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteTarget(reg)}
                                    title="Supprimer l'inscription"
                                    className="p-1.5 rounded-lg text-red-600 hover:bg-red-100 transition-colors"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Supprimer l'inscription</h3>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              <p className="font-medium mb-1">Attention</p>
              <p>La suppression d'une inscription ne doit etre utilisee qu'en cas de reinscription (quand un parent souhaite reinscrire son enfant et que l'ancienne inscription annulee bloque la nouvelle).</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
              <p><span className="font-medium text-gray-700">Enfant :</span> {selectedKid?.prenom} {selectedKid?.nom}</p>
              <p><span className="font-medium text-gray-700">Stage :</span> {deleteTarget.session.stage?.title}</p>
              <p><span className="font-medium text-gray-700">Centre :</span> {deleteTarget.session.center?.name}</p>
              <p><span className="font-medium text-gray-700">Semaine :</span> {deleteTarget.session.semaine}</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Suppression...' : 'Supprimer definitivement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate confirmation modal */}
      {reactivateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RotateCcw className="h-5 w-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reactiver l'inscription</h3>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
              <p className="font-medium mb-1">Information</p>
              <p>L'inscription sera remise en actif. L'enfant apparaitra a nouveau dans les listes de presences. Le statut de paiement restera inchange.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
              <p><span className="font-medium text-gray-700">Enfant :</span> {selectedKid?.prenom} {selectedKid?.nom}</p>
              <p><span className="font-medium text-gray-700">Stage :</span> {reactivateTarget.session.stage?.title}</p>
              <p><span className="font-medium text-gray-700">Centre :</span> {reactivateTarget.session.center?.name}</p>
              <p><span className="font-medium text-gray-700">Semaine :</span> {reactivateTarget.session.semaine}</p>
              <p><span className="font-medium text-gray-700">Paiement :</span> {reglementACoteDeLAnnulation(
                { annulation: reactivateTarget.cancellation_status, commande: reactivateTarget.payment_status },
                lignesDe(reglements, reactivateTarget.id),
              ).libelle}</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setReactivateTarget(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleReactivate}
                disabled={reactivating}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {reactivating ? 'Reactivation...' : 'Reactiver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer modal */}
      {transferTarget && (
        <GroupTransferModal
          registration={transferTarget}
          onClose={() => setTransferTarget(null)}
          onSuccess={handleTransferSuccess}
        />
      )}
    </div>
  )
}
